import moment from 'moment';
import {
  Village,
  VillageDeal,
  VillageExtra,
  VillagePrice,
  VillagePricePeriod,
  VillagePriceVariants,
} from '@/services/types';
import { pugliaMap, sicilyMap, villagesMap } from './maps';
import { GBP_MULTIPLIER, USD_MULTIPLIER } from './currency';

export function getVillageSlug(villageTitle: string) {
  return villageTitle
    .split("'")
    .join('')
    .split(' ')
    .join('-')
    .toLocaleLowerCase();
}

export function getVillageLink({
  lang,
  slug,
  region,
  filterGuests,
  filterDates,
}: {
  lang: string;
  slug: string;
  region: string;
  filterGuests?: {
    adults: number;
    children: number;
    infants: number;
  };
  filterDates?: {
    start: Date | null;
    end: Date | null;
  };
}) {
  const regionLabel = region == 'sicily' ? sicilyMap[lang] : pugliaMap[lang];
  let url = `/${regionLabel}/${villagesMap[lang]}/${slug}`;

  if (lang !== 'en') {
    url = `/${lang}/${regionLabel}/${villagesMap[lang]}/${slug}`;
  }

  const datesParams = new URLSearchParams();

  if (filterDates) {
    if (filterDates.start && filterDates.end) {
      const { start, end } = filterDates;
      datesParams.append('start', moment(start).format('yyyy-MM-DD'));
      datesParams.append('end', moment(end).format('yyyy-MM-DD'));
    }

    if (datesParams.toString().length > 0) {
      url = `${url}?${datesParams.toString()}`;
    }
  }

  if (filterGuests) {
    let guestsParams = new URLSearchParams();
    if (filterGuests.adults > 0) {
      guestsParams.append('adults', filterGuests.adults.toString());
    }
    if (filterGuests.infants > 0) {
      guestsParams.append('infants', filterGuests.infants.toString());
    }
    if (filterGuests.children > 0) {
      guestsParams.append('children', filterGuests.children.toString());
    }
    if (guestsParams.toString().length > 0) {
      if (datesParams.toString().length > 0) {
        url = `${url}&${guestsParams.toString()}`;
      } else {
        url = `${url}?${guestsParams.toString()}`;
      }
    }
  }

  return url;
}

export function countRangeDays(rangeValue: {
  start: Date | null;
  end: Date | null;
}) {
  if (rangeValue?.start && rangeValue?.end) {
    const oneDay = 24 * 60 * 60 * 1000;
    const diffInMilliseconds = Math.abs(
      rangeValue.end.getTime() - rangeValue.start.getTime(),
    );
    const diffInDays = Math.round(diffInMilliseconds / oneDay);
    return diffInDays;
  }
}

export function formatVillagePrice(price: number, lang: string) {
  const formatter = new Intl.NumberFormat('en-DE', {
    style: 'currency',
    currency: lang == 'en' ? 'USD' : 'EUR',
    currencyDisplay: 'symbol',
  });

  let formattedPrice = formatter.format(price).replace(/^(\D+)/, '$1 ');

  formattedPrice = formattedPrice.replace(/(\.0+|,) *$/g, '');

  if (lang === 'it' || lang === 'de') {
    formattedPrice = formattedPrice.replace(/,/g, '.');
  }

  if (lang === 'fr') {
    formattedPrice = formattedPrice.replace(/,/g, '');
  }

  return formattedPrice;
}

export function getSicilyAirportName(lang: string, latitude: number) {
  if (latitude < 13) {
    return 'Trapani';
  }

  if (latitude < 14 && latitude > 12) {
    if (lang == 'fr') {
      return 'Palerme';
    } else {
      return 'Palermo';
    }
  }

  if (latitude < 15 && latitude > 13) {
    return 'Comiso';
  }

  if (latitude < 16 && latitude > 14) {
    if (lang == 'fr') {
      return 'Catane';
    } else {
      return 'Catania';
    }
  }
  return '';
}

export function findVillagePriceVariant(
  priceVariants: VillagePriceVariants[],
  datesRange: { start: Date | null; end: Date | null },
) {
  let targetVariant: VillagePriceVariants | undefined;
  let variantPeriod: VillagePricePeriod | undefined;
  const currentYear = new Date().getFullYear();
  const currentPriceVariants = priceVariants.filter(
    (variant) => variant.villaPricing.year === currentYear,
  );

  currentPriceVariants.forEach((variant) => {
    const findedPeriod = variant.villaPricing.pricePeriods.find((period) => {
      const startPeriodTime = new Date(period.startDate).getTime();
      const endPeriodTime = new Date(period.endDate).getTime();
      if (datesRange.start && datesRange.end) {
        return (
          datesRange.start.getTime() >= startPeriodTime &&
          datesRange.end.getTime() <= endPeriodTime
        );
      }
    });
    if (findedPeriod) {
      variantPeriod = findedPeriod;
      targetVariant = priceVariants.filter((variant) =>
        variant.villaPricing.pricePeriods.includes(findedPeriod),
      )[0];
    }
  });

  return {
    variantPeriod,
    targetVariant,
  };
}

export function getVillageWishListInfos(village: Village, currency: string) {
  const villageMainFeatures = village.features?.filter(({ feature }) => {
    return feature?.type ? feature.type === 'main_feature' : false;
  });

  const villageMedia = village.media[0].mediaFilters?.find(
    (img) => img.name === 'medium_3-2',
  )?.url;

  const wishListInfos = {
    id: village.id,
    title: village.title,
    sleeps: village.sleeps,
    bedrooms: village.bedrooms,
    bathrooms: village.bathrooms,
    city: village.city,
    media: villageMedia ? villageMedia : '',
    prices: {
      originalPrice: village.prices[0]?.originalPrice,
      ownerPrice: village.prices[0]?.ownerPrice,
      currency: currency,
    },
    zone: {
      code: village.zone.code,
      enabled: true,
    },
    villageMainFeatures: villageMainFeatures,
    slug: village.slug,
  };

  return wishListInfos;
}

export function villageHasVisiblePromotion(deals: VillageDeal[]) {
  if (deals && deals.length > 0) {
    const hasPromotion = deals.some((deal) => {
      const onRange = moment(deal.deal.visibleTo).isSameOrAfter(moment());
      const isEnabled = deal.enabled && deal.deal.publicVisible;
      return onRange && isEnabled;
    });
    return hasPromotion;
  }

  return false;
}

export function applyCurrency(value: string, currency?: string) {
  let formattedValue = parseFloat(value);

  if (currency == 'usd') {
    return String(formattedValue * USD_MULTIPLIER);
  }

  if (currency == 'gbp') {
    return String(formattedValue * GBP_MULTIPLIER);
  }

  return String(formattedValue);
}

export function getVillageWithCurrencyApplied(
  village: Village,
  currency?: string,
) {
  const priceVariants = village.priceVariants.map((priceVariant) => {
    const securityDeposit = applyCurrency(
      priceVariant.securityDeposit,
      currency,
    );

    const newPriceVariant: VillagePriceVariants = {
      ...priceVariant,
      securityDeposit,
    };
    return newPriceVariant;
  });

  const deals = village.deals.map((deal) => {
    const value = applyCurrency(deal.deal.value, currency);

    const newDeal: VillageDeal = {
      ...deal,
      deal: {
        ...deal.deal,
        value,
      },
    };
    return newDeal;
  });

  const extras = village.extras.map((extra) => {
    const newPrice = applyCurrency(extra.price, currency);

    const newExtra: VillageExtra = {
      ...extra,
      price: newPrice,
    };

    return newExtra;
  });

  const prices = village.prices.map(({ period, variant, ...price }) => {
    const securityDeposit = applyCurrency(variant.securityDeposit, currency);

    const originalPrice = applyCurrency(price.originalPrice, currency);

    const ownerPrice = applyCurrency(price.ownerPrice, currency);

    const newPrice: VillagePrice = {
      period,
      variant: {
        ...variant,
        securityDeposit,
      },
      originalPrice,
      ownerPrice,
    };
    return newPrice;
  });

  const villageWithCurrencyApplied: Village = {
    ...village,
    prices,
    priceVariants,
    extras,
    deals,
  };

  return villageWithCurrencyApplied;
}
